<script lang="ts" setup>
import { ref } from "vue"
import axios from "axios"

const email = ref("")
const firstName = ref("")
const lastName = ref("")
const password = ref("")
const error = ref("")
const success = ref(false)
const loading = ref(false)

async function handleSignup() {
    error.value = ""
    loading.value = true
    try {
        await axios.post("/testup-auth/auth/register", {
            email: email.value,
            firstName: firstName.value,
            lastName: lastName.value,
            password: password.value
        })
        success.value = true
    } catch (e: any) {
        error.value = e.response?.data?.error || "Registration failed"
    } finally {
        loading.value = false
    }
}
</script>

<template lang="pug">
.auth-page
    .auth-card
        img.auth-logo(src="@/assets/images/testup-icon-rahmenlos-300x220.png")
        h2 Sign Up
        template(v-if="success")
            .auth-success
                i.fas.fa-check-circle
                p Account created successfully.
                p An administrator must approve your account before you can log in.
            .auth-footer
                router-link(to="/login") Back to Login
        template(v-else)
            .auth-error(v-if="error") {{ error }}
            form(@submit.prevent="handleSignup")
                .form-row
                    .form-group
                        label(for="firstName") First Name
                        input#firstName(v-model="firstName" type="text" placeholder="First name" required)
                    .form-group
                        label(for="lastName") Last Name
                        input#lastName(v-model="lastName" type="text" placeholder="Last name" required)
                .form-group
                    label(for="email") Email
                    input#email(
                        v-model="email"
                        type="email"
                        placeholder="you@example.com"
                        required
                        autocomplete="email"
                    )
                .form-group
                    label(for="password") Password
                    input#password(
                        v-model="password"
                        type="password"
                        placeholder="At least 8 characters"
                        required
                        minlength="8"
                        autocomplete="new-password"
                    )
                button.auth-btn(type="submit" :disabled="loading")
                    | {{ loading ? "Creating account..." : "Create Account" }}
            .auth-footer
                | Already have an account?&nbsp;
                router-link(to="/login") Login
</template>

<style scoped>
.auth-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: linear-gradient(160deg, var(--c-bg) 0%, var(--c-bg-soft) 100%);
}

.auth-card {
    background: white;
    padding: 48px 40px;
    border-radius: var(--r-card);
    box-shadow: var(--shadow-card-hover);
    max-width: 440px;
    width: 100%;
    text-align: center;
}

.auth-logo {
    width: 60px;
    margin-bottom: 16px;
}

h2 {
    margin: 0 0 24px 0;
    color: var(--black);
}

.auth-error {
    background: #fef2f2;
    color: var(--red);
    padding: 10px 14px;
    border-radius: var(--r-btn);
    margin-bottom: 16px;
    font-size: 14px;
}

.auth-success {
    padding: 20px;
    text-align: center;
    color: var(--green);
}

.auth-success i {
    font-size: 48px;
    margin-bottom: 16px;
}

.auth-success p {
    margin: 8px 0;
    color: #333;
    font-size: 14px;
}

.form-row {
    display: flex;
    gap: 12px;
}

.form-group {
    margin-bottom: 16px;
    text-align: left;
    flex: 1;
}

.form-group label {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.form-group input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--light-grey);
    border-radius: var(--r-btn);
    font-size: 14px;
    box-sizing: border-box;
}

.form-group input:focus {
    outline: none;
    border-color: var(--green);
    box-shadow: 0 0 0 2px rgba(17, 140, 24, 0.15);
}

.auth-btn {
    width: 100%;
    padding: 12px;
    background: var(--green);
    color: white;
    border: none;
    border-radius: var(--r-btn);
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
}

.auth-btn:hover:not(:disabled) {
    background: var(--dark-green);
}

.auth-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.auth-footer {
    margin-top: 20px;
    font-size: 14px;
    color: var(--dark-grey);
}

.auth-footer a {
    color: var(--green);
    text-decoration: none;
    font-weight: 500;
}
</style>
